import { DateTime } from 'luxon';

/**
 * t291u: in-chat search helpers. The search itself runs on the server (scoped to this conversation, permission-checked);
 * these work out which words to highlight, where they sit in a piece of text, which match is next, and what to say.
 */

/** The words to highlight: the query split on spaces, longest first so "deliver" wins over "de". */
export function searchTerms(q: string): string[] {
  const words = q.trim().split(/\s+/).filter(Boolean).slice(0, 8).map(w => w.toLowerCase());
  return [...new Set(words)].sort((a, b) => b.length - a.length);
}

/** [start, end) ranges of the words in `text`, case-insensitive, sorted and never overlapping. */
export function hitRanges(text: string, terms: string[]): [number, number][] {
  if (!text || !terms.length) return [];
  const lower = text.toLowerCase();
  const out: [number, number][] = [];
  for (const t of terms) {
    for (let i = lower.indexOf(t); i >= 0; i = lower.indexOf(t, i + t.length)) out.push([i, i + t.length]);
  }
  out.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const merged: [number, number][] = [];
  for (const r of out) {
    const last = merged.at(-1);
    if (last && r[0] < last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  return merged;
}

/**
 * Step through matches, numbered newest first ("1 of 12" is the newest). Next (Enter, ↑) goes one older, previous
 * (Shift+Enter, ↓) one newer; both wrap round.
 */
export function stepMatch(index: number, total: number, dir: 1 | -1): number {
  if (total <= 0) return -1;
  if (index < 0) return 0;
  return (index + dir + total) % total;
}

/** "3 of 12", "No matches", or "Searching…". */
export function matchCounter(index: number, total: number, opts: { loading?: boolean; truncated?: boolean } = {}): string {
  if (opts.loading) return 'Searching…';
  if (total === 0) return 'No matches';
  return `${index + 1} of ${total}${opts.truncated ? '+' : ''}`;
}

/** What Jump to date says when it lands, or when there's nothing to show. Dates are London dates (yyyy-MM-dd). */
export function jumpToDateNote(picked: string, landed: string | null): string | null {
  const day = DateTime.fromISO(picked, { zone: 'Europe/London' });
  const label = (d: DateTime) => d.toFormat('ccc d LLL yyyy');
  if (!landed) return `No messages on or after ${label(day)}.`;
  const at = DateTime.fromISO(landed, { zone: 'utc' }).setZone('Europe/London');
  if (at.toISODate() === picked) return null;
  return `No messages on ${label(day)}. Showing the next day with messages, ${label(at)}.`;
}

/** London calendar date (yyyy-MM-dd) of a timestamp, for the day pills' picker. */
export const londonDate = (iso: string) => DateTime.fromISO(iso, { zone: 'utc' }).setZone('Europe/London').toISODate()!;

const HL = 'chat-search-hit';
const HL_CURRENT = 'chat-search-current';
type HighlightRegistry = { set(name: string, h: unknown): void; delete(name: string): void };
const registry = (): HighlightRegistry | null => (typeof CSS !== 'undefined' && 'highlights' in CSS ? (CSS as unknown as { highlights: HighlightRegistry }).highlights : null);
const HighlightCtor = (): (new (...r: Range[]) => unknown) | null => (typeof window !== 'undefined' && 'Highlight' in window ? (window as unknown as { Highlight: new (...r: Range[]) => unknown }).Highlight : null);

/**
 * Highlight the words inside `root` with the CSS Custom Highlight API (::highlight in index.css): a background mark
 * with black text, painted without touching the DOM React owns. Only text inside message bodies is marked
 * ([data-message-body]); the current match's message gets the stronger mark. Returns a cleanup.
 */
export function paintHighlights(root: HTMLElement | null, terms: string[], currentId?: string | null): () => void {
  const reg = registry(); const H = HighlightCtor();
  const clear = () => { reg?.delete(HL); reg?.delete(HL_CURRENT); };
  if (!reg || !H || !root || !terms.length) { clear(); return clear; }
  const all: Range[] = []; const cur: Range[] = [];
  for (const body of root.querySelectorAll<HTMLElement>('[data-message-body], [data-attachment-name]')) {
    const row = body.closest<HTMLElement>('[id^="m-"]');
    const isCurrent = Boolean(currentId && row?.id === `m-${currentId}`);
    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      for (const [s, e] of hitRanges(n.textContent ?? '', terms)) {
        const r = document.createRange(); r.setStart(n, s); r.setEnd(n, e);
        (isCurrent ? cur : all).push(r);
      }
    }
  }
  reg.set(HL, new H(...all)); reg.set(HL_CURRENT, new H(...cur));
  return clear;
}

/** Briefly flash a message row (the bubble when there is one). */
export function flashMessage(id: string) {
  const row = document.getElementById(`m-${id}`);
  if (!row) return false;
  const target = row.querySelector<HTMLElement>('[data-testid=bubble]') ?? row;
  target.classList.add('chat-jump-flash');
  setTimeout(() => target.classList.remove('chat-jump-flash'), 1800);
  return true;
}
